import { Monitor, Moon, Sun } from 'lucide-react'
import * as React from 'react'
import { Button } from '@/components/ui/button'

/**
 * Light and dark are both real targets, not a default and an afterthought.
 * Both are the same custom properties with different values (`styles.css`), so
 * nothing in the app branches on theme — a component that needs an `isDark`
 * check is a component whose colours are hard-coded.
 *
 * Three states, not two. "System" has to be reachable: a user who followed
 * their OS, toggled once to look at something, and can no longer get back is
 * left permanently out of step with every other app on their machine.
 */

const KEY = 'era.meet.theme'
type Preference = 'light' | 'dark' | 'system'

function systemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** The same rule the inline script in index.html runs before first paint. */
function resolveTheme(preference: Preference): boolean {
  return preference === 'dark' || (preference === 'system' && systemDark())
}

function stored(): Preference {
  const value = localStorage.getItem(KEY)
  return value === 'dark' || value === 'light' ? value : 'system'
}

export function ThemeToggle({ className }: { className?: string }) {
  const [preference, setPreference] = React.useState<Preference>(stored)

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', resolveTheme(preference))
    if (preference === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, preference)

    if (preference !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => document.documentElement.classList.toggle('dark', media.matches)
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [preference])

  const next: Record<Preference, Preference> = { system: 'dark', dark: 'light', light: 'system' }
  const Icon = preference === 'dark' ? Moon : preference === 'light' ? Sun : Monitor

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onClick={() => setPreference(next[preference])}
      title={`Theme: ${preference}. Click for ${next[preference]}.`}
      aria-label={`Theme: ${preference}`}
    >
      <Icon />
    </Button>
  )
}
